'use client';

import Link from 'next/link';
import { use } from 'react';
import { Badge, Box, Card, Divider, Grid, Image, Text } from '@mantine/core';
import { NEWS_CATEGORY } from '@/shared/const/news';
import { beautifyDate } from '@/shared/libs/dates';
import { components } from '@/shared/types/api/TeraApi';
import PageChanger from '@/app/components/pagination/Pagination';
import { useTranslation } from '@/i18n/client';

export function NewsListNewsItem({ news }: { news: components['schemas']['NewsItemDTO'] }) {
  const { t, lng } = useTranslation('news');
  return (
    <Card withBorder p={0} mb={10}>
      <Grid>
        <Grid.Col span={{ base: 12, md: 4 }} style={{ display: 'flex', width: '100px' }} p={0}>
          <Image
            src={`/static/images/news/mid/${news.category}.webp`}
            alt='category'
            style={{ width: '100%', height: '100%', flex: 'none' }}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 8 }} px={0} py={15} style={{ position: 'relative' }}>
          <Box px={15}>
            <h2 style={{ margin: 0, paddingLeft: '5px', paddingTop: '5px', lineHeight: '1.1em' }}>
              {news.title[lng]}
            </h2>
            <Badge
              variant='light'
              color={
                news.category === NEWS_CATEGORY.PROJECT_NEWS
                  ? 'green'
                  : news.category === NEWS_CATEGORY.EVENTS
                    ? 'blue'
                    : news.category === NEWS_CATEGORY.NOTES
                      ? 'yellow'
                      : news.category === NEWS_CATEGORY.MAINTENANCE
                        ? 'red'
                        : 'black'
              }
            >
              {t('category.' + news.category)}
            </Badge>
          </Box>
          <Text pt={10} pb={50} px={20}>
            {news.short[lng]}
          </Text>
          <Box style={{ position: 'absolute', bottom: 0, right: 0, width: '100%' }}>
            <Divider m={0} />
            <Grid pt={10} pb={15} px={20}>
              <Grid.Col span={{ base: 6 }}>
                <time dateTime={news.date}>{beautifyDate(news.date, lng)}</time>
              </Grid.Col>
              <Grid.Col span={{ base: 6 }} style={{ textAlign: 'center' }}>
                <Link
                  className='btn btn-tertiary btn-lg'
                  href={'/news/full/' + news.id}
                  style={{
                    color: 'var(--text-color)',
                    float: 'right',
                    marginRight: '10px',
                    fontSize: '16px',
                    textDecoration: 'none',
                  }}
                >
                  {t('read_more')}
                </Link>
              </Grid.Col>
            </Grid>
          </Box>
        </Grid.Col>
      </Grid>
    </Card>
  );
}

export function NewsListNews({
  data,
  page,
}: {
  data: Promise<components['schemas']['NewsListDTO'] | undefined>;
  page: number;
}) {
  const newsData = use(data);

  if (!newsData) {
    return null;
  }

  return (
    <>
      <Box mb={10}>
        {newsData.news.map((news, index) => (
          <NewsListNewsItem key={index} news={news} />
        ))}
      </Box>
      {newsData.totalPages > 1 && (
        <Box py={20}>
          <PageChanger totalPages={newsData.totalPages} currentPage={page} link='/news' />
        </Box>
      )}
    </>
  );
}

export default NewsListNews;
